import React, { useState } from 'react';
import '../../styles/neo.css';

/**
 * /design-system — aperçu visuel du design system néo-brutaliste web.
 * Miroir de la route /design-system de l'app native. Page de démo :
 * aucune logique métier, sert à valider les tokens et composants.
 */

const Section = ({ title, children }) => (
    <section style={{ marginBottom: 40 }}>
        <p className="neo-label tone-soft" style={{ marginBottom: 12 }}>{title}</p>
        {children}
    </section>
);

const Row = ({ children, wrap = true }) => (
    <div style={{ display: 'flex', gap: 16, flexWrap: wrap ? 'wrap' : 'nowrap', alignItems: 'center' }}>
        {children}
    </div>
);

const Swatch = ({ name, varName, ink = '#fff' }) => (
    <div
        className="neo-box"
        style={{
            background: `var(${varName})`,
            width: 110,
            height: 72,
            display: 'flex',
            alignItems: 'flex-end',
            padding: 8,
        }}
    >
        <span className="neo-caption" style={{ color: ink }}>{name}</span>
    </div>
);

const DesignSystem = () => {
    const [theme, setTheme] = useState('light');
    const [toggleOn, setToggleOn] = useState(true);
    const [chipSelected, setChipSelected] = useState('Idées');
    const [segment, setSegment] = useState('notes');

    return (
        <div className="neo-page" data-theme={theme} style={{ padding: '32px 20px' }}>
            <div style={{ maxWidth: 860, margin: '0 auto' }}>

                <Row>
                    <div style={{ flex: 1 }}>
                        <h1 className="neo-display">Design system</h1>
                        <p className="neo-body tone-soft">Echonote — néo-brutaliste, version web.</p>
                    </div>
                    <div className="neo-segmented">
                        <button
                            className={`neo-segment ${theme === 'light' ? 'is-active' : ''}`}
                            onClick={() => setTheme('light')}
                        >
                            ☀ Clair
                        </button>
                        <button
                            className={`neo-segment ${theme === 'dark' ? 'is-active' : ''}`}
                            onClick={() => setTheme('dark')}
                        >
                            ☾ Sombre
                        </button>
                    </div>
                </Row>

                <hr className="neo-hr" />

                <Section title="Couleurs">
                    <Row>
                        <Swatch name="primary" varName="--neo-primary" />
                        <Swatch name="accent" varName="--neo-accent" ink="#121212" />
                        <Swatch name="success" varName="--neo-success" ink="#121212" />
                        <Swatch name="warning" varName="--neo-warning" ink="#121212" />
                        <Swatch name="error" varName="--neo-error" />
                    </Row>
                </Section>

                <Section title="Typographie">
                    <p className="neo-display">Display 900</p>
                    <p className="neo-h1">Titre H1 — 900</p>
                    <p className="neo-h2">Titre H2 — 800</p>
                    <p className="neo-h3">Titre H3 — 800</p>
                    <p className="neo-body">Body — texte courant, lisible et posé sur la page.</p>
                    <p className="neo-small tone-soft">Small — texte secondaire adouci.</p>
                    <p className="neo-label">Label — majuscules espacées</p>
                    <p className="neo-body">
                        Un <a className="neo-link" href="#top">lien néo-brutaliste</a> dans le texte.
                    </p>
                </Section>

                <Section title="Boutons">
                    <Row>
                        <button className="neo-btn">Primary</button>
                        <button className="neo-btn neo-btn--accent">Accent</button>
                        <button className="neo-btn neo-btn--surface">Surface</button>
                        <button className="neo-btn neo-btn--danger">Danger</button>
                        <button className="neo-btn" disabled>Désactivé</button>
                    </Row>
                    <div style={{ height: 16 }} />
                    <Row>
                        <button className="neo-btn neo-btn--sm">Petit</button>
                        <button className="neo-btn neo-btn--lg neo-btn--accent">Grand</button>
                        <button className="neo-icon-btn" title="Ajouter">＋</button>
                        <button className="neo-icon-btn neo-icon-btn--primary" title="Lecture">▶</button>
                        <button className="neo-icon-btn neo-icon-btn--accent" title="Micro">🎙</button>
                        <button className="neo-icon-btn neo-icon-btn--danger" title="Supprimer">✕</button>
                    </Row>
                </Section>

                <Section title="Cartes">
                    <Row>
                        <div className="neo-card" style={{ flex: 1, minWidth: 220 }}>
                            <span className="neo-badge">Note</span>
                            <h3 className="neo-h3" style={{ margin: '10px 0 4px' }}>Carte simple</h3>
                            <p className="neo-small tone-soft">Bordure épaisse, ombre dure, à-plat franc.</p>
                        </div>
                        <div className="neo-card neo-card--press" style={{ flex: 1, minWidth: 220 }}>
                            <span className="neo-badge" style={{ background: 'var(--neo-primary)', color: '#fff' }}>To-do</span>
                            <h3 className="neo-h3" style={{ margin: '10px 0 4px' }}>Carte cliquable</h3>
                            <p className="neo-small tone-soft">Survolez puis cliquez : elle s'enfonce vers son ombre.</p>
                        </div>
                        <div className="neo-card neo-card--alt neo-card--flat" style={{ flex: 1, minWidth: 220 }}>
                            <h3 className="neo-h3" style={{ margin: '0 0 4px' }}>Carte plate</h3>
                            <p className="neo-small tone-soft">Surface alternative, sans ombre — pour l'imbriqué.</p>
                        </div>
                    </Row>
                </Section>

                <Section title="Stats">
                    <Row>
                        <div className="neo-stat">
                            <p className="neo-stat__value">128</p>
                            <p className="neo-stat__label">Notes</p>
                        </div>
                        <div className="neo-stat" style={{ background: 'var(--neo-primary)', color: '#fff' }}>
                            <p className="neo-stat__value">3 h 24</p>
                            <p className="neo-stat__label">Audio</p>
                        </div>
                        <div className="neo-stat" style={{ background: 'var(--neo-surface)', color: 'var(--neo-ink)' }}>
                            <p className="neo-stat__value">42</p>
                            <p className="neo-stat__label">To-dos</p>
                        </div>
                    </Row>
                </Section>

                <Section title="Chips & badges">
                    <Row>
                        {['Idées', 'Travail', 'Perso'].map((tag) => (
                            <button
                                key={tag}
                                className={`neo-chip ${chipSelected === tag ? 'is-selected' : ''}`}
                                onClick={() => setChipSelected(tag)}
                            >
                                {tag}
                            </button>
                        ))}
                        <span className="neo-chip">
                            <span className="neo-chip__dot" style={{ background: 'var(--neo-accent)' }} />
                            Projet
                            <button className="neo-chip__remove" title="Retirer">✕</button>
                        </span>
                        <span className="neo-badge">Pro</span>
                        <span className="neo-badge" style={{ background: 'var(--neo-blue)', color: '#fff' }}>Nouveau</span>
                    </Row>
                </Section>

                <Section title="Formulaires">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 420 }}>
                        <div className="neo-field">
                            <label className="neo-label" htmlFor="ds-email">E-mail</label>
                            <input id="ds-email" className="neo-input" type="email" placeholder="vous@exemple.com" />
                        </div>
                        <div className="neo-field">
                            <label className="neo-label" htmlFor="ds-pwd">Mot de passe</label>
                            <input id="ds-pwd" className="neo-input has-error" type="password" defaultValue="1234" />
                            <p className="neo-field__error">Mot de passe trop court.</p>
                        </div>
                        <div className="neo-field">
                            <label className="neo-label" htmlFor="ds-note">Note</label>
                            <textarea id="ds-note" className="neo-textarea" placeholder="Écrivez quelque chose…" />
                        </div>
                        <div className="neo-field">
                            <label className="neo-label" htmlFor="ds-lang">Langue</label>
                            <div className="neo-select">
                                <select id="ds-lang" defaultValue="fr">
                                    <option value="fr">Français</option>
                                    <option value="en">English</option>
                                    <option value="es">Español</option>
                                </select>
                            </div>
                        </div>
                        <Row>
                            <label className="neo-small" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <input
                                    type="checkbox"
                                    className="neo-toggle"
                                    checked={toggleOn}
                                    onChange={(e) => setToggleOn(e.target.checked)}
                                />
                                Résumé automatique
                            </label>
                        </Row>
                        <input type="range" className="neo-range" defaultValue={60} />
                    </div>
                </Section>

                <Section title="Segmenté">
                    <div className="neo-segmented" style={{ width: 320, display: 'flex' }}>
                        {[['notes', 'Notes'], ['audio', 'Audio'], ['todos', 'To-dos']].map(([val, label]) => (
                            <button
                                key={val}
                                className={`neo-segment ${segment === val ? 'is-active' : ''}`}
                                onClick={() => setSegment(val)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </Section>

                <Section title="Liste">
                    <div className="neo-card" style={{ padding: 4 }}>
                        <button className="neo-list-item">
                            <span className="neo-list-item__icon">🎙</span>
                            <span className="neo-list-item__body">
                                <span className="neo-h3" style={{ display: 'block' }}>Enregistrement</span>
                                <span className="neo-small tone-soft" style={{ display: 'block' }}>Qualité, format, micro</span>
                            </span>
                            <span className="neo-list-item__chevron">›</span>
                        </button>
                        <hr className="neo-hr" style={{ margin: 0 }} />
                        <div className="neo-list-item">
                            <span className="neo-list-item__icon" style={{ background: 'var(--neo-primary)', color: '#fff' }}>☾</span>
                            <span className="neo-list-item__body">
                                <span className="neo-h3" style={{ display: 'block' }}>Mode sombre</span>
                                <span className="neo-small tone-soft" style={{ display: 'block' }}>Suivre le système</span>
                            </span>
                            <input
                                type="checkbox"
                                className="neo-toggle"
                                checked={theme === 'dark'}
                                onChange={(e) => setTheme(e.target.checked ? 'dark' : 'light')}
                            />
                        </div>
                    </div>
                </Section>

                <Section title="Toasts">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
                        <div className="neo-toast neo-toast--success">
                            <span className="neo-toast__icon">✓</span>
                            <div>
                                <p className="neo-h3">Note enregistrée</p>
                                <p className="neo-small tone-soft">Votre note a bien été transcrite.</p>
                            </div>
                        </div>
                        <div className="neo-toast neo-toast--error">
                            <span className="neo-toast__icon">!</span>
                            <div>
                                <p className="neo-h3">Échec de l'envoi</p>
                                <p className="neo-small tone-soft">Vérifiez votre connexion.</p>
                            </div>
                        </div>
                        <div className="neo-toast neo-toast--info">
                            <span className="neo-toast__icon">i</span>
                            <div>
                                <p className="neo-h3">Astuce</p>
                                <p className="neo-small tone-soft">Maintenez le micro pour un mémo rapide.</p>
                            </div>
                        </div>
                    </div>
                </Section>

            </div>
        </div>
    );
};

export default DesignSystem;
